import React from 'react';

// instead of using random emoji everywhere, we draw all our icons as svg paths
// right here on the same 24x24 grid with the same stroke width, so they look
// like they belong together.
// each key below is an icon name, and the value is the actual svg shape(s) for it.
const PATHS = {
  // the little disguise/mask icon - our whole brand thing
  disguise: (
    <>
      <path d="M3 8.5C3 7 4 6 5.5 6h13C20 6 21 7 21 8.5c0 4-2 7-5 7-1.6 0-2.4-.9-3-1.7h-2c-.6.8-1.4 1.7-3 1.7-3 0-5-3-5-7z" />
      <path d="M6.5 9.5h2M15.5 9.5h2" />
    </>
  ),
  // sparkle - used for premium/ai/fancy stuff
  sparkle: (
    <path d="M12 3c.4 3.6 1.4 4.6 5 5-3.6.4-4.6 1.4-5 5-.4-3.6-1.4-4.6-5-5 3.6-.4 4.6-1.4 5-5zM18.5 13.5c.2 1.6.7 2.1 2.3 2.3-1.6.2-2.1.7-2.3 2.3-.2-1.6-.7-2.1-2.3-2.3 1.6-.2 2.1-.7 2.3-2.3z" />
  ),
  // the lightning bolt - means fast/instant
  zap: <polygon points="13 2 4 14 11 14 10 22 20 9 13 9 13 2" />,
  // shield with a checkmark - the "you're safe / private" icon
  shield: (
    <>
      <path d="M12 21s7-3.5 7-9V6l-7-3-7 3v6c0 5.5 7 9 7 9z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),
  // artists palette - for the themes feature
  palette: (
    <>
      <path d="M12 3a9 9 0 0 0 0 18 2.5 2.5 0 0 0 2.5-2.5c0-.6-.2-1.1-.6-1.5-.3-.4-.5-.8-.5-1.3A1.7 1.7 0 0 1 14.6 14H17a4 4 0 0 0 4-4c0-3.9-4-7-9-7z" />
      <circle cx="7.5" cy="11" r="1" />
      <circle cx="10" cy="7.5" r="1" />
      <circle cx="14.5" cy="7.5" r="1" />
    </>
  ),
  // the little keyboard/command icon - for keyboard shortcuts
  command: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M8 14h8" />
    </>
  ),
  // stacked layers - used for the "session memory" feature
  layers: (
    <>
      <path d="M12 3 3 8l9 5 9-5-9-5z" />
      <path d="M3 13l9 5 9-5M3 8" />
    </>
  ),
  // a 2x2 grid of squares - used for the excel/spreadsheet disguise
  grid: (
    <>
      <rect x="4" y="4" width="6.5" height="6.5" rx="1" />
      <rect x="13.5" y="4" width="6.5" height="6.5" rx="1" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1" />
      <rect x="4" y="13.5" width="6.5" height="6.5" rx="1" />
    </>
  ),
  // a clock - means "coming soon" / time related
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.8" />
    </>
  ),
  // a globe - for anything web/firefox/worldwide
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.5 2.3 3.8 5.3 3.8 8.5s-1.3 6.2-3.8 8.5c-2.5-2.3-3.8-5.3-3.8-8.5S9.5 5.8 12 3.5z" />
    </>
  ),
  // plain checkmark - "this is active / yes / done"
  check: <path d="M5 12.5 10 17l9-10" />,
  // a padlock - shows a feature is locked behind premium
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
  // a science flask - we use it to mean "alpha / experimental"
  flask: (
    <>
      <path d="M9.5 3h5M10.5 3v5.2L6.3 16a2 2 0 0 0 1.8 3h7.8a2 2 0 0 0 1.8-3l-4.2-7.8V3" />
      <path d="M8 14h8" />
    </>
  ),
  // a cute robot head - for ai / more disguises coming
  robot: (
    <>
      <rect x="5" y="8" width="14" height="11" rx="2.5" />
      <path d="M12 5v3M9.5 13h.01M14.5 13h.01M9 19v1M15 19v1" />
    </>
  ),
};

// this is the actual component you drop into pages like <Icon name="zap" />
// name = which icon, size = how big, className = extra styling, strokeWidth = line thickness
export default function Icon({ name, size = 24, className, strokeWidth = 1.6 }) {
  // look up the shape for the name you asked for
  const path = PATHS[name];
  // if you typed a name we don't have, just render nothing instead of crashing
  if (!path) return null;
  // otherwise wrap the shape in a proper svg tag with all our shared styling
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"      // inherits whatever text color the parent has, handy
      strokeWidth={strokeWidth}
      strokeLinecap="round"      // rounded ends so lines look soft, not sharp
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"         // hide from screen readers since it's decorative
    >
      {path}
    </svg>
  );
}
